// ============================================================================
// BrightPath - Notification Types
// BP-12-03: Notifications Frontend
// ============================================================================

// ----------------------------------------------------------------------------
// Enums
// ----------------------------------------------------------------------------

export enum NotificationCategory {
  APPOINTMENT = 'appointment',
  HEALTH = 'health',
  DOCUMENT = 'document',
  COMMUNITY = 'community',
  LEGAL = 'legal',
  MARKETPLACE = 'marketplace',
  SYSTEM = 'system',
}

export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
}

export enum NotificationChannel {
  IN_APP = 'in_app',
  EMAIL = 'email',
  PUSH = 'push',
  SMS = 'sms',
}

// ----------------------------------------------------------------------------
// Core Notification Types
// ----------------------------------------------------------------------------

export interface Notification {
  id: string;
  userId: string;
  notificationType: string;
  category: NotificationCategory;
  title: string;
  body: string;
  actionUrl?: string;
  actionData?: Record<string, unknown>;
  priority: NotificationPriority;
  isRead: boolean;
  readAt?: string;
  emailSent: boolean;
  emailSentAt?: string;
  pushSent: boolean;
  pushSentAt?: string;
  smsSent: boolean;
  smsSentAt?: string;
  expiresAt?: string;
  createdAt: string;
}

export interface NotificationGroup {
  date: string;
  notifications: Notification[];
}

// ----------------------------------------------------------------------------
// Notification Preferences
// ----------------------------------------------------------------------------

export interface NotificationPreference {
  id: string;
  userId: string;
  category: NotificationCategory;
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  quietHoursStart?: string; // HH:MM format
  quietHoursEnd?: string;   // HH:MM format
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPreferencesMap {
  [key: string]: NotificationPreference;
}

export interface GlobalNotificationSettings {
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  timezone: string;
}

// ----------------------------------------------------------------------------
// Push Subscription
// ----------------------------------------------------------------------------

export interface PushSubscription {
  id: string;
  userId: string;
  endpoint: string;
  p256dhKey: string;
  authKey: string;
  deviceType: 'web' | 'ios' | 'android';
  createdAt: string;
}

export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: PushSubscriptionKeys;
}

// ----------------------------------------------------------------------------
// API Request/Response Types
// ----------------------------------------------------------------------------

export interface NotificationListParams {
  page?: number;
  pageSize?: number;
  category?: NotificationCategory;
  isRead?: boolean;
  priority?: NotificationPriority;
  startDate?: string;
  endDate?: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  unreadCount: number;
}

export interface UnreadCountResponse {
  total: number;
  byCategory: Record<NotificationCategory, number>;
}

export interface MarkReadRequest {
  notificationIds: string[];
}

export interface MarkReadResponse {
  success: boolean;
  markedCount: number;
}

export interface UpdatePreferencesRequest {
  category: NotificationCategory;
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  smsEnabled?: boolean;
}

export interface UpdateGlobalSettingsRequest {
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  smsEnabled?: boolean;
  quietHoursEnabled?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone?: string;
}

export interface SubscribePushRequest {
  endpoint: string;
  p256dhKey: string;
  authKey: string;
  deviceType: 'web' | 'ios' | 'android';
}

export interface TestNotificationRequest {
  channel: NotificationChannel;
  category?: NotificationCategory;
}

// ----------------------------------------------------------------------------
// WebSocket Types
// ----------------------------------------------------------------------------

export enum WebSocketMessageType {
  NOTIFICATION = 'notification',
  NOTIFICATION_READ = 'notification_read',
  UNREAD_COUNT = 'unread_count',
  PING = 'ping',
  PONG = 'pong',
  ERROR = 'error',
}

export interface WebSocketMessage {
  type: WebSocketMessageType;
  payload: unknown;
  timestamp: string;
}

export interface NewNotificationMessage {
  type: WebSocketMessageType.NOTIFICATION;
  payload: Notification;
  timestamp: string;
}

export interface UnreadCountMessage {
  type: WebSocketMessageType.UNREAD_COUNT;
  payload: UnreadCountResponse;
  timestamp: string;
}

// ----------------------------------------------------------------------------
// Toast/UI Types
// ----------------------------------------------------------------------------

export interface ToastNotification {
  id: string;
  notification: Notification;
  isVisible: boolean;
  duration?: number;
  onDismiss?: () => void;
  onAction?: () => void;
}

export interface NotificationFilterState {
  category: NotificationCategory | 'all';
  isRead: 'all' | 'read' | 'unread';
  priority: NotificationPriority | 'all';
  dateRange: 'all' | 'today' | 'week' | 'month';
}

// ----------------------------------------------------------------------------
// Component Props Types
// ----------------------------------------------------------------------------

export interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (notification: Notification) => void;
  compact?: boolean;
}

export interface NotificationListProps {
  notifications: Notification[];
  loading?: boolean;
  error?: string | null;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onMarkRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  onNotificationClick?: (notification: Notification) => void;
  emptyMessage?: string;
  groupByDate?: boolean;
}

export interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onViewAll?: () => void;
  maxDisplayCount?: number;
}

export interface NotificationBadgeProps {
  count: number;
  max?: number;
  showZero?: boolean;
  onClick?: () => void;
  className?: string;
}

export interface NotificationPreferencesProps {
  preferences: NotificationPreference[];
  globalSettings: GlobalNotificationSettings;
  onUpdatePreference: (request: UpdatePreferencesRequest) => Promise<void>;
  onUpdateGlobalSettings: (request: UpdateGlobalSettingsRequest) => Promise<void>;
  loading?: boolean;
}

export interface PushPermissionPromptProps {
  onEnable: () => Promise<void>;
  onDismiss: () => void;
  isLoading?: boolean;
}

export interface ToastNotificationProps {
  toast: ToastNotification;
  onDismiss: (id: string) => void;
  onAction?: (notification: Notification) => void;
}

// ----------------------------------------------------------------------------
// Utility Types
// ----------------------------------------------------------------------------

export type NotificationCategoryConfig = {
  [key in NotificationCategory]: {
    label: string;
    icon: string;
    color: string;
    description: string;
  };
};

export const NOTIFICATION_CATEGORY_CONFIG: NotificationCategoryConfig = {
  [NotificationCategory.APPOINTMENT]: {
    label: 'Appointments',
    icon: 'Calendar',
    color: 'sage',
    description: 'Appointment reminders and updates',
  },
  [NotificationCategory.HEALTH]: {
    label: 'Health',
    icon: 'Heart',
    color: 'terracotta',
    description: 'Health tracking and medication reminders',
  },
  [NotificationCategory.DOCUMENT]: {
    label: 'Documents',
    icon: 'FileText',
    color: 'navy',
    description: 'Document uploads and sharing',
  },
  [NotificationCategory.COMMUNITY]: {
    label: 'Community',
    icon: 'Users',
    color: 'blue',
    description: 'Forum replies and community activity',
  },
  [NotificationCategory.LEGAL]: {
    label: 'Legal',
    icon: 'Scale',
    color: 'navy',
    description: 'Case updates and attorney communications',
  },
  [NotificationCategory.MARKETPLACE]: {
    label: 'Marketplace',
    icon: 'ShoppingCart',
    color: 'terracotta',
    description: 'Order updates and vendor messages',
  },
  [NotificationCategory.SYSTEM]: {
    label: 'System',
    icon: 'Settings',
    color: 'gray',
    description: 'Platform announcements and account alerts',
  },
};

export type NotificationPriorityConfig = {
  [key in NotificationPriority]: {
    label: string;
    color: string;
    bgColor: string;
  };
};

export const NOTIFICATION_PRIORITY_CONFIG: NotificationPriorityConfig = {
  [NotificationPriority.LOW]: {
    label: 'Low',
    color: 'text-warm-gray',
    bgColor: 'bg-cream',
  },
  [NotificationPriority.NORMAL]: {
    label: 'Normal',
    color: 'text-navy',
    bgColor: 'bg-cream',
  },
  [NotificationPriority.HIGH]: {
    label: 'High',
    color: 'text-terracotta',
    bgColor: 'bg-terracotta/10',
  },
  [NotificationPriority.URGENT]: {
    label: 'Urgent',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
};
